/**
 * The Execute portfolio — the set of use cases the user has proceeded with.
 *
 * This is a read/write view over EAContext.finalistIds, which already persists to
 * localStorage and drives /activate. There is deliberately no separate portfolio store:
 * "finalist" (Evaluate's word) and "initiative in the Execute portfolio" are the same set.
 */

import { useCallback, useMemo } from 'react';
import { useEA } from '../contexts/EAContext';

export interface ProceedResult {
  /** True when the use case was already in the portfolio — proceed was a no-op. */
  existing: boolean;
}

export function useExecutePortfolio() {
  const { finalistIds, finalistOrder, addFinalist, removeFinalist } = useEA();

  // finalistOrder is synced to finalistIds by an effect in EAContext, so on the render
  // right after addFinalist it does not yet contain the new id. Append any stragglers
  // rather than waiting a frame for them to appear.
  const portfolio = useMemo(() => {
    const ordered = finalistOrder.filter((id) => finalistIds.has(id));
    const seen = new Set(ordered);
    return [...ordered, ...[...finalistIds].filter((id) => !seen.has(id))];
  }, [finalistOrder, finalistIds]);

  const isInPortfolio = useCallback((id: string) => finalistIds.has(id), [finalistIds]);

  /** Adds a use case to the Execute portfolio. Idempotent. */
  const proceed = useCallback(
    (id: string): ProceedResult => {
      if (finalistIds.has(id)) return { existing: true };
      addFinalist(id);
      return { existing: false };
    },
    [finalistIds, addFinalist],
  );

  return {
    portfolio,
    count: portfolio.length,
    isInPortfolio,
    proceed,
    remove: removeFinalist,
  };
}

export default useExecutePortfolio;
